/**
 * UK Postcode and Address lookup
 * 
 * Uses postcodes.io for postcode validation (free, no API key)
 * Uses getAddress.io for property-level address lookup
 */

export interface PostcodeLookupResult {
  postcode: string
  city: string
  region: string
  country: string
  latitude: number
  longitude: number
}

export interface AddressResult {
  id: string
  line1: string
  line2: string
  line3: string
  city: string
  county: string
  country: string
  postcode: string
  formatted: string
  buildingName: string
  buildingNumber: string
  thoroughfare: string
  latitude?: number
  longitude?: number
}

// getAddress.io API key
const GETADDRESS_API_KEY = 'NtoFlLgZIUmduoTfXXHGSg49730'

/**
 * Look up all addresses at a UK postcode using getAddress.io autocomplete API
 */
export async function lookupAddresses(postcode: string): Promise<AddressResult[]> {
  const cleaned = postcode.trim().replace(/\s+/g, '').toUpperCase()
  
  if (!cleaned) {
    throw new Error('Please enter a postcode')
  }

  // First get coordinates from postcodes.io (free)
  let coordinates: { lat: number; lng: number } | null = null
  
  try {
    const postcodeData = await lookupPostcode(cleaned)
    coordinates = { lat: postcodeData.latitude, lng: postcodeData.longitude }
  } catch {
    throw new Error('Invalid postcode. Please check and try again.')
  }

  // Use getAddress.io autocomplete with all=true to get all addresses at postcode
  try {
    const response = await fetch(
      `https://api.getAddress.io/autocomplete/${encodeURIComponent(cleaned)}?api-key=${GETADDRESS_API_KEY}&all=true`
    )
    
    if (response.ok) {
      const data = await response.json()
      
      if (data.suggestions && Array.isArray(data.suggestions) && data.suggestions.length > 0) {
        return data.suggestions.map((suggestion: any) => ({
          id: suggestion.id,
          formatted: suggestion.address,
          // Parse the formatted address to extract parts
          line1: suggestion.address.split(',')[0]?.trim() || '',
          line2: suggestion.address.split(',')[1]?.trim() || '',
          line3: suggestion.address.split(',')[2]?.trim() || '',
          city: extractCity(suggestion.address),
          county: '',
          country: 'United Kingdom',
          postcode: extractPostcode(suggestion.address) || cleaned,
          buildingName: '',
          buildingNumber: '',
          thoroughfare: '',
          latitude: coordinates?.lat,
          longitude: coordinates?.lng,
        }))
      }
    } else if (response.status === 404) {
      throw new Error('No addresses found for this postcode')
    }
  } catch (err) {
    if (err instanceof Error && err.message.includes('No addresses')) {
      throw err
    }
    // Fall through to manual entry
  }

  throw new Error('Address lookup unavailable. Please enter your address manually.')
}

/**
 * Get full address details from a getAddress.io suggestion ID
 */
export async function getAddressDetails(id: string): Promise<AddressResult | null> {
  try {
    const response = await fetch(
      `https://api.getAddress.io/get/${encodeURIComponent(id)}?api-key=${GETADDRESS_API_KEY}`
    )
    
    if (response.ok) {
      const addr = await response.json()
      
      return {
        id,
        line1: addr.line_1 || '',
        line2: addr.line_2 || '',
        line3: addr.line_3 || '',
        city: addr.town_or_city || '',
        county: addr.county || '',
        country: addr.country || 'United Kingdom',
        postcode: addr.postcode || '',
        formatted: addr.formatted_address?.filter(Boolean).join(', ') || '',
        buildingName: addr.building_name || '',
        buildingNumber: addr.building_number || '',
        thoroughfare: addr.thoroughfare || '',
        latitude: addr.latitude,
        longitude: addr.longitude,
      }
    }
  } catch {
    // Ignore errors
  }
  
  return null
}

/**
 * Extract city from a formatted address string
 */
function extractCity(address: string): string {
  const parts = address.split(',').map(p => p.trim())
  // City is usually the second-to-last or third-to-last part before postcode
  if (parts.length >= 3) {
    // Check if last part looks like a postcode
    const lastPart = parts[parts.length - 1]
    if (/^[A-Z]{1,2}\d/.test(lastPart)) {
      return parts[parts.length - 2] || ''
    }
    return parts[parts.length - 1] || ''
  }
  return ''
}

/**
 * Extract postcode from a formatted address string
 */
function extractPostcode(address: string): string {
  const match = address.match(/[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}/i)
  return match ? match[0] : ''
}

/**
 * Look up basic postcode information from postcodes.io (free)
 */
export async function lookupPostcode(postcode: string): Promise<PostcodeLookupResult> {
  const cleaned = postcode.trim().replace(/\s+/g, '')
  
  if (!cleaned) {
    throw new Error('Please enter a postcode')
  }
  
  const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(cleaned)}`)
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Postcode not found')
    }
    throw new Error('Failed to look up postcode')
  }
  
  const data = await response.json()
  
  if (data.status !== 200 || !data.result) {
    throw new Error('Invalid postcode')
  }
  
  const result = data.result
  
  return {
    postcode: result.postcode,
    city: result.admin_district || result.region || '',
    region: result.region || '',
    country: result.country || 'United Kingdom',
    latitude: result.latitude,
    longitude: result.longitude,
  }
}

/**
 * Generate a Google Maps URL from coordinates
 */
export function generateGoogleMapsUrl(latitude: number, longitude: number): string {
  return `https://www.google.com/maps?q=${latitude},${longitude}`
}
